import React from 'react';
import { Button } from './ui';
import { IconAlert } from './icons';
import type { ClassifiedError } from '../utils/errors';

interface ErrorViewProps {
  error: ClassifiedError;
  onRetry?: () => void;
  onClose: () => void;
}

/**
 * Classified error screen. Renders a title + remediation from the error
 * taxonomy. Retry button is hidden when the error is non-retryable (e.g.
 * tampered PDF, quota exceeded).
 */
export const ErrorView: React.FC<ErrorViewProps> = ({ error, onRetry, onClose }) => (
  <div style={{ textAlign: 'center', padding: '20px 0' }}>
    <span
      style={{
        display: 'inline-flex',
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: 'var(--pc-color-error-bg)',
        color: 'var(--pc-color-error)',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    >
      <IconAlert size={28} />
    </span>
    <h3
      style={{
        fontSize: 18,
        fontWeight: 600,
        color: 'var(--pc-color-text)',
        margin: '0 0 8px',
      }}
    >
      {error.title}
    </h3>
    <p
      style={{
        fontSize: 14,
        color: 'var(--pc-color-text-muted)',
        margin: '0 0 12px',
        lineHeight: 1.5,
      }}
    >
      {error.message}
    </p>
    {error.remediation && (
      <p
        style={{
          fontSize: 13,
          color: 'var(--pc-color-text-muted)',
          margin: '0 auto 22px',
          lineHeight: 1.5,
          maxWidth: 320,
        }}
      >
        <strong style={{ color: 'var(--pc-color-text)' }}>Try this:</strong>{' '}
        {error.remediation}
      </p>
    )}

    <div
      style={{
        display: 'flex',
        gap: 10,
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      {error.retryable && onRetry && (
        <Button variant="primary" fullWidth onClick={onRetry}>
          Try again
        </Button>
      )}
      <Button
        variant={error.retryable ? 'secondary' : 'primary'}
        fullWidth
        onClick={onClose}
      >
        Close
      </Button>
    </div>
  </div>
);
